import { createFileRoute, Link } from "@tanstack/react-router";
import {
  Building2,
  HeartPulse,
  IndianRupee,
  Users,
  ArrowUpRight,
  AlertTriangle,
  ShieldCheck,
  TrendingUp,
} from "lucide-react";
import { toast } from "sonner";

import { Panel, StatCard } from "@/components/dashboard-shell";
import { formatINR } from "@/data/sample";
import {
  useApplications,
  useCampaigns,
  useDonations,
  useOrganisations,
  useReviewApplication,
} from "@/lib/queries";
import {
  TrendAreaChart,
  ComboBarLineChart,
  DonutChart,
  HBarChart,
  RadialGauge,
  SparkLine,
} from "@/components/charts";
import { AIFraudDetector } from "@/components/ai-fraud-detector";
import { AICauseMatcher } from "@/components/ai-cause-matcher";
import { AIAdminCopilot } from "@/components/ai-admin-copilot";

export const Route = createFileRoute("/admin/")({
  component: AdminDashboard,
});

function AdminDashboard() {
  const { data: orgs = [] } = useOrganisations({ all: true });
  const { data: campaigns = [] } = useCampaigns({ all: true });
  const { data: donations = [] } = useDonations();
  const { data: applications = [] } = useApplications();
  const review = useReviewApplication();

  const platformDonations = donations.reduce((s, d) => s + (d.amount ?? 0), 0);
  const lifetime = orgs.reduce((s, o) => s + (o.total_raised ?? 0), 0) + platformDonations;
  const donorCount = new Set(donations.map((d) => d.donor_email ?? d.donor_id ?? d.id)).size;
  const pendingApps = applications.filter((a) => a.status === "pending");

  // 12-week live donation trend
  const now = Date.now();
  const trendSeries = Array.from({ length: 12 }, (_, i) => {
    const start = now - (12 - i) * 7 * 864e5;
    const end = start + 7 * 864e5;
    const weekDonations = donations.filter((d) => {
      const t = new Date(d.created_at).getTime();
      return t >= start && t < end;
    });
    return {
      label: `W${i + 1}`,
      value: weekDonations.reduce((s, d) => s + d.amount, 0),
      donations: weekDonations.reduce((s, d) => s + d.amount, 0),
      donors: new Set(weekDonations.map((d) => d.donor_email ?? d.donor_id ?? d.id)).size,
    };
  });
  const sparkTrend = trendSeries.map((t) => t.value);

  // Top organisations by raised (live)
  const topOrgs = [...orgs]
    .sort((a, b) => (b.total_raised ?? 0) - (a.total_raised ?? 0))
    .slice(0, 6)
    .map((o) => ({
      label: o.name.length > 22 ? o.name.slice(0, 22) + "…" : o.name,
      value: o.total_raised ?? 0,
    }));

  // Cause mix by campaign category
  const catMap = new Map<string, number>();
  campaigns.forEach((c) => {
    const key = (c as any).category ?? (c as any).tags?.[0] ?? "General";
    catMap.set(key, (catMap.get(key) ?? 0) + (c.raised ?? 0));
  });
  const causeMix = Array.from(catMap.entries())
    .map(([name, value]) => ({ name, value }))
    .sort((a, b) => b.value - a.value)
    .slice(0, 6);
  if (causeMix.length === 0) causeMix.push({ name: "General fund", value: platformDonations || 1 });

  const approved = orgs.filter((o) => o.status === "approved").length;
  const verificationPct = orgs.length ? Math.round((approved / orgs.length) * 100) : 0;
  const inr = (v: number) => formatINR(v);

  async function act(id: string, status: "approved" | "rejected") {
    try {
      await review.mutateAsync({ id, status });
      toast.success(
        status === "approved" ? "Organisation approved and published." : "Application rejected.",
      );
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Action failed — super admin access required.");
    }
  }

  return (
    <>
      <div className="mb-7 rounded-3xl bg-white border border-border/80 p-6 sm:p-8 shadow-xs">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <span className="inline-flex items-center gap-1.5 rounded-full bg-[var(--saffron)]/20 px-3 py-1 text-xs font-extrabold uppercase tracking-wider text-[var(--maroon-deep)] border border-[var(--saffron-deep)]/30">
              <ShieldCheck className="h-3.5 w-3.5 text-[var(--saffron-deep)]" /> Super Admin ·
              Foundation Console
            </span>
            <h1 className="mt-3 font-display text-2xl font-extrabold sm:text-3xl text-[var(--maroon-deep)]">
              Platform Overview
            </h1>
            <p className="mt-1.5 max-w-xl text-sm text-foreground/70 leading-relaxed">
              Live snapshot of partner organisations, campaigns, and donations flowing through
              Manavta across India.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-[var(--cream-2)] px-3.5 py-1.5 text-xs font-semibold text-[var(--maroon-deep)] border border-border/60">
              <ShieldCheck className="h-3.5 w-3.5 text-[var(--saffron-deep)]" /> 80G · 12A Live
            </span>
            <span className="inline-flex items-center gap-1.5 rounded-full bg-[var(--cream-2)] px-3.5 py-1.5 text-xs font-semibold text-[var(--maroon-deep)] border border-border/60">
              <TrendingUp className="h-3.5 w-3.5 text-emerald-600" /> +18% MoM
            </span>
          </div>
        </div>
      </div>

      {/* ── AI Super Admin Command Center ───────────── */}
      <div className="mb-7">
        <AIAdminCopilot />
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <RichStat
          icon={Building2}
          label="Partner organisations"
          value={String(orgs.length)}
          delta={`${orgs.filter((o) => o.status !== "approved").length} awaiting review`}
          tint="var(--maroon)"
          spark={sparkTrend}
        />
        <RichStat
          icon={HeartPulse}
          label="Campaigns"
          value={String(campaigns.length)}
          delta={`${campaigns.filter((c) => c.status === "pending").length} pending approval`}
          tint="var(--saffron-deep)"
          spark={sparkTrend}
        />
        <RichStat
          icon={IndianRupee}
          label="Lifetime raised"
          value={formatINR(lifetime)}
          delta={`${formatINR(platformDonations)} on platform`}
          tint="var(--success)"
          spark={sparkTrend}
        />
        <RichStat
          icon={Users}
          label="Donors on platform"
          value={String(donorCount)}
          delta={`${donations.length} gifts recorded`}
          tint="#5b6ee1"
          spark={sparkTrend}
        />
      </div>

      <div className="mt-6 grid gap-4 lg:grid-cols-3">
        <div className="lg:col-span-2">
          <Panel
            title="Donations & donors · last 12 weeks"
            action={<span className="text-xs text-foreground/70">Live · weekly</span>}
          >
            <ComboBarLineChart data={trendSeries} valueFormatter={inr} />
          </Panel>
        </div>
        <Panel
          title="Verification funnel"
          action={<span className="text-xs text-foreground/70">Onboarding</span>}
        >
          <RadialGauge
            value={verificationPct}
            label="Approved"
            hint={`${approved} of ${orgs.length} orgs live`}
          />
          <ul className="mt-3 space-y-1.5 text-xs">
            <li className="flex justify-between">
              <span className="text-foreground/70">Approved</span>
              <span className="font-semibold text-[var(--success)]">{approved}</span>
            </li>
            <li className="flex justify-between">
              <span className="text-foreground/70">Pending review</span>
              <span className="font-semibold text-[var(--saffron-deep)]">{pendingApps.length}</span>
            </li>
            <li className="flex justify-between">
              <span className="text-foreground/70">Rejected</span>
              <span className="font-semibold text-foreground/70">
                {applications.filter((a) => a.status === "rejected").length}
              </span>
            </li>
          </ul>
        </Panel>
      </div>

      <div className="mt-6 grid gap-4 lg:grid-cols-3">
        <div className="lg:col-span-2">
          <Panel title="Top partner organisations · lifetime raised">
            <HBarChart
              data={topOrgs.length ? topOrgs : [{ label: "No data yet", value: 0 }]}
              valueFormatter={inr}
            />
          </Panel>
        </div>
        <Panel title="Cause mix by category">
          <DonutChart data={causeMix} valueFormatter={inr} />
        </Panel>
      </div>

      <div className="mt-6 grid gap-4 lg:grid-cols-3">
        <div className="lg:col-span-2">
          <Panel
            title="Daily inflow trend"
            action={<span className="text-xs text-foreground/70">Rolling</span>}
          >
            <TrendAreaChart data={trendSeries} valueFormatter={inr} />
          </Panel>
        </div>
        <Panel title="System health">
          <ul className="space-y-3 text-sm">
            <HealthRow label="Razorpay gateway" status="ok" note="99.98% uptime" />
            <HealthRow label="Receipt engine" status="ok" note="Instant PDF" />
            <HealthRow label="Bank verification" status="ok" note="Penny drop ready" />
            <HealthRow label="WhatsApp channel" status="warn" note="Beta" />
          </ul>
        </Panel>
      </div>

      <div className="mt-6 grid gap-4 lg:grid-cols-2">
        <Panel
          title="Pending verifications"
          action={
            <Link
              to="/admin/applications"
              className="rounded-full bg-[var(--accent)] px-2 py-0.5 text-[10px] font-semibold text-[var(--maroon-deep)]"
            >
              {pendingApps.length} pending
            </Link>
          }
        >
          <ul className="divide-y divide-border text-sm">
            {pendingApps.slice(0, 5).map((a) => (
              <li key={a.id} className="flex items-center justify-between py-3">
                <div className="flex items-center gap-3 min-w-0">
                  <span className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-[var(--cream-2)] text-[var(--maroon)]">
                    <Building2 className="h-4 w-4" />
                  </span>
                  <div className="min-w-0">
                    <p className="truncate font-medium">{a.org_name}</p>
                    <p className="text-xs text-foreground/70">
                      {a.city ?? "—"} · {a.org_type ?? "Organisation"}
                    </p>
                  </div>
                </div>
                <div className="flex shrink-0 gap-2">
                  <button
                    onClick={() => act(a.id, "approved")}
                    className="rounded-full bg-[var(--success)] px-3 py-1 text-xs font-semibold text-white"
                  >
                    Approve
                  </button>
                  <button
                    onClick={() => act(a.id, "rejected")}
                    className="rounded-full bg-[var(--destructive)] px-3 py-1 text-xs font-semibold text-white"
                  >
                    Reject
                  </button>
                </div>
              </li>
            ))}
            {pendingApps.length === 0 && (
              <p className="py-3 text-sm text-foreground/70">No applications awaiting review.</p>
            )}
          </ul>
        </Panel>
        <Panel
          title="Recent donations"
          action={
            <Link to="/admin/donations" className="text-xs text-[var(--maroon)]">
              View all
            </Link>
          }
        >
          <ul className="divide-y divide-border text-sm">
            {donations.slice(0, 5).map((d) => (
              <li key={d.id} className="flex items-center justify-between py-3">
                <div className="flex items-center gap-3 min-w-0">
                  <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-[var(--accent)] text-xs font-bold text-[var(--maroon-deep)]">
                    {(d.anonymous ? "A" : d.donor_name).charAt(0)}
                  </span>
                  <div className="min-w-0">
                    <p className="truncate font-medium">
                      {d.anonymous ? "Anonymous donor" : d.donor_name}
                    </p>
                    <p className="truncate text-xs text-foreground/70">
                      {d.target_label ?? "General fund"} · {d.receipt_no ?? "receipt pending"}
                    </p>
                  </div>
                </div>
                <span className="font-semibold text-[var(--maroon)]">{formatINR(d.amount)}</span>
              </li>
            ))}
            {donations.length === 0 && (
              <p className="py-3 text-sm text-foreground/70">No donations recorded yet.</p>
            )}
          </ul>
        </Panel>
      </div>

      <div className="mt-6 grid gap-4 lg:grid-cols-3">
        <StatCard
          label="Today's donations"
          value={formatINR(
            donations
              .filter((d) => new Date(d.created_at).toDateString() === new Date().toDateString())
              .reduce((s, d) => s + d.amount, 0),
          )}
          hint={`${donations.filter((d) => new Date(d.created_at).toDateString() === new Date().toDateString()).length} gifts`}
          tone="primary"
        />
        <StatCard
          label="This month"
          value={formatINR(
            donations
              .filter((d) => new Date(d.created_at).getMonth() === new Date().getMonth())
              .reduce((s, d) => s + d.amount, 0),
          )}
          hint="Recorded on platform"
          tone="primary"
        />
        <div className="rounded-2xl border border-amber-200 bg-amber-50/50 p-5 shadow-xs">
          <div className="flex items-center gap-2 text-amber-700">
            <AlertTriangle className="h-4 w-4" />
            <p className="text-xs font-bold uppercase tracking-wider">Attention needed</p>
          </div>
          <p className="mt-2 font-display text-2xl font-bold text-[var(--maroon-deep)]">
            {campaigns.filter((c) => c.status === "pending").length} campaigns pending
          </p>
          <p className="mt-1 text-xs text-foreground/60">
            Waiting for foundation approval before going live
          </p>
          <Link
            to="/admin/causes"
            className="mt-3 inline-flex items-center gap-1 text-xs font-bold text-[var(--maroon)]"
          >
            Review now <ArrowUpRight className="h-3 w-3" />
          </Link>
        </div>
      </div>

      {/* ── AI Intelligence Layer ────────────────────── */}
      <div className="mt-6 grid gap-4 lg:grid-cols-3">
        <div className="lg:col-span-2">
          <AICauseMatcher preferences={["monthly"]} />
        </div>
        <div>
          {pendingApps.length > 0 ? (
            <AIFraudDetector
              application={{
                org_name: pendingApps[0].org_name,
                reg_80g: (pendingApps[0] as any).reg_80g,
                reg_12a: (pendingApps[0] as any).reg_12a,
                registration_no: (pendingApps[0] as any).registration_no ?? "REG12345",
                animals_cared: (pendingApps[0] as any).animals_cared ?? 200,
                about: (pendingApps[0] as any).about,
                city: pendingApps[0].city ?? "Ahmedabad",
              }}
            />
          ) : (
            <AIFraudDetector
              application={{
                org_name: "Demo Organisation",
                reg_80g: "80G/2023/DEMO",
                reg_12a: "12A/2020/DEMO",
                registration_no: "REG12345",
                animals_cared: 150,
                about:
                  "A verified animal welfare organisation operating for over 10 years in Gujarat.",
                city: "Ahmedabad",
              }}
            />
          )}
        </div>
      </div>
    </>
  );
}

function RichStat({
  icon: Icon,
  label,
  value,
  delta,
  tint,
  spark,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  value: string;
  delta: string;
  tint: string;
  spark?: number[];
}) {
  return (
    <div className="relative overflow-hidden rounded-2xl border border-border/80 bg-card p-5 shadow-xs transition hover:shadow-sm">
      <div className="absolute top-0 inset-x-0 h-1" style={{ background: tint }} />
      <div className="flex items-start justify-between">
        <span
          className="grid h-10 w-10 place-items-center rounded-xl text-white shadow-xs"
          style={{ background: tint }}
        >
          <Icon className="h-5 w-5" />
        </span>
        <span className="inline-flex items-center gap-1 rounded-full bg-[var(--cream-2)] px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-foreground/75 border border-border/60">
          {delta}
        </span>
      </div>
      <p className="mt-4 font-display text-2xl font-extrabold text-[var(--maroon-deep)]">{value}</p>
      <p className="mt-1 text-xs font-bold uppercase tracking-wider text-foreground/60">{label}</p>
      {spark && spark.length > 1 && (
        <div className="mt-3 -mx-1">
          <SparkLine data={spark} color={tint} />
        </div>
      )}
    </div>
  );
}

function HealthRow({
  label,
  status,
  note,
}: {
  label: string;
  status: "ok" | "warn";
  note: string;
}) {
  const color = status === "ok" ? "var(--success)" : "var(--saffron-deep)";
  return (
    <li className="flex items-center justify-between">
      <div className="flex items-center gap-2">
        <span className="h-2 w-2 rounded-full" style={{ background: color }} />
        <span>{label}</span>
      </div>
      <span className="text-xs text-foreground/70">{note}</span>
    </li>
  );
}
